import React, { useCallback, useEffect, useState } from "react";
import { Loader2, LogOut, UserCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateTime } from "@/lib/format";
import AccountAccess from "@/components/AccountAccess";
import { ErrorBox, Field, PasswordRules, dangerBtn, ghostBtn, inputCls, passwordProblems, primaryBtn } from "@/components/auth/AuthUi";

function Panel({ title, children, testId }) {
  return (
    <section className="term-panel rounded-md p-4 md:p-5 space-y-4" data-testid={testId}>
      <h2 className="text-sm font-bold tracking-tight">{title}</h2>
      {children}
    </section>
  );
}

export default function Account() {
  const { user, refreshUser, setSession, logout, can } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [nameBusy, setNameBusy] = useState(false);

  const [emailPw, setEmailPw] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);

  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [pwErr, setPwErr] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  const [sessions, setSessions] = useState([]);

  const loadSessions = useCallback(() => {
    api.get("/auth/sessions").then((r) => setSessions(r.data.sessions || [])).catch(() => {});
  }, []);
  useEffect(() => { loadSessions(); }, [loadSessions]);

  const saveName = async (e) => {
    e.preventDefault();
    setNameBusy(true);
    try { await api.patch("/auth/me", { name }); await refreshUser(); toast.success("Name updated"); }
    catch (e2) { toast.error(apiError(e2)); }
    finally { setNameBusy(false); }
  };

  const changeEmail = async (e) => {
    e.preventDefault();
    setEmailErr(""); setEmailBusy(true);
    try {
      await api.post("/auth/change-email", { password: emailPw, new_email: newEmail });
      await refreshUser();
      setEmailPw(""); setNewEmail("");
      loadSessions();
      toast.success("Email updated. Other sessions were signed out.");
    } catch (e2) { setEmailErr(apiError(e2)); }
    finally { setEmailBusy(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwErr(""); setPwBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: cur, new_password: next });
      setSession(data);
      setCur(""); setNext(""); setConfirm("");
      loadSessions();
      toast.success("Password changed. Other sessions were signed out.");
    } catch (e2) { setPwErr(apiError(e2)); }
    finally { setPwBusy(false); }
  };

  const signOutEverywhere = async () => {
    if (!window.confirm("Sign out of every device, including this one?")) return;
    try { await api.post("/auth/logout-all"); } catch { /* token already invalid */ }
    logout();
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-5" data-testid="account-page">
      <div className="flex items-center gap-3">
        <UserCircle2 className="h-7 w-7 text-[#2563EB]" />
        <div>
          <h1 className="text-lg font-bold tracking-tight">My Account</h1>
          <div className="text-xs text-slate-500">{user?.email} · {(user?.roles || []).join(", ")} · last sign-in {dateTime(user?.last_login_at)}</div>
        </div>
      </div>

      <Panel title="Trading access" testId="account-trading-access">
        <AccountAccess user={user} can={can} />
      </Panel>

      <Panel title="Profile" testId="account-profile">
        <form onSubmit={saveName} className="flex items-end gap-3">
          <div className="flex-1"><Field label="Name">
            <input data-testid="account-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} maxLength={120} />
          </Field></div>
          <button type="submit" disabled={nameBusy || !name.trim() || name.trim() === user?.name} className={primaryBtn} data-testid="account-name-save">
            {nameBusy && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </button>
        </form>
      </Panel>

      <Panel title="Change sign-in email" testId="account-email">
        <form onSubmit={changeEmail} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="New email"><input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className={inputCls} data-testid="account-new-email" /></Field>
          <Field label="Current password"><input type="password" autoComplete="current-password" value={emailPw} onChange={(e) => setEmailPw(e.target.value)} className={inputCls} data-testid="account-email-password" /></Field>
          <div className="md:col-span-2 space-y-2">
            <ErrorBox>{emailErr}</ErrorBox>
            <button type="submit" disabled={emailBusy || !newEmail || !emailPw} className={primaryBtn} data-testid="account-email-save">
              {emailBusy && <Loader2 className="h-4 w-4 animate-spin" />} Update email
            </button>
          </div>
        </form>
      </Panel>

      <Panel title="Change password" testId="account-password">
        <form onSubmit={changePassword} className="space-y-3">
          <Field label="Current password"><input type="password" autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} className={inputCls} data-testid="account-current-password" /></Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="New password"><input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} className={inputCls} data-testid="account-new-password" /></Field>
            <Field label="Confirm new password"><input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} data-testid="account-confirm-password" /></Field>
          </div>
          <PasswordRules password={next} confirm={confirm} email={user?.email} />
          <ErrorBox>{pwErr}</ErrorBox>
          <button type="submit" disabled={pwBusy || !cur || passwordProblems(next, confirm, user?.email).length > 0} className={primaryBtn} data-testid="account-password-save">
            {pwBusy && <Loader2 className="h-4 w-4 animate-spin" />} Change password
          </button>
        </form>
      </Panel>

      <Panel title="Active sessions" testId="account-sessions">
        <div className="text-[11px] text-slate-500">Sessions last up to 12 hours and end after 2 hours without activity.</div>
        <div className="divide-y divide-[#20242c] border border-[#2A2F3A] rounded-sm">
          {sessions.length === 0 && <div className="p-3 text-xs text-slate-500">No active sessions.</div>}
          {sessions.map((s, i) => (
            <div key={i} className="p-3 text-xs flex flex-wrap gap-x-4 gap-y-1">
              <span className="text-slate-200">{s.current ? "This session" : `Session ${s.session_id_hint}…`}</span>
              <span className="text-slate-500">signed in {dateTime(s.created_at)}</span>
              <span className="text-slate-500">last active {dateTime(s.last_seen_at)}</span>
              <span className="text-slate-500">expires {dateTime(s.expires_at)}</span>
              <span className="text-slate-600 truncate max-w-[260px]" title={s.user_agent || ""}>{s.ip || ""} {s.user_agent || ""}</span>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={logout} className={ghostBtn} data-testid="account-logout"><LogOut className="h-4 w-4" /> Sign out</button>
          <button onClick={signOutEverywhere} className={dangerBtn} data-testid="account-logout-all">Sign out everywhere</button>
        </div>
      </Panel>
    </div>
  );
}
